import { useState, useEffect, useCallback } from "react";
import {
  Page,
  Card,
  DataTable,
  Badge,
  Button,
  EmptyState,
  Spinner,
  Banner,
  ButtonGroup,
} from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import {
  getCampaigns,
  deleteCampaign,
  updateCampaign,
  type Campaign,
} from "../api/campaigns";

function CampaignsListPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getCampaigns();
      setCampaigns(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const handleStatusChange = async (id: string, newStatus: Campaign["status"]) => {
    try {
      await updateCampaign(id, { status: newStatus });
      await loadCampaigns();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update campaign status");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to archive this campaign?")) {
      return;
    }

    try {
      await deleteCampaign(id);
      await loadCampaigns();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete campaign");
    }
  };

  const getStatusBadge = (status: Campaign["status"]) => {
    switch (status) {
      case "ACTIVE":
        return <Badge tone="success">Active</Badge>;
      case "PAUSED":
        return <Badge tone="warning">Paused</Badge>;
      case "DRAFT":
        return <Badge>Draft</Badge>;
      case "ENDED":
        return <Badge tone="info">Ended</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const rows = campaigns.map((campaign) => {
    const variantCount = campaign.variants?.length || 0;
    const sessionCount = campaign._count?.sessions || 0;

    return [
      campaign.name,
      campaign.placement,
      getStatusBadge(campaign.status),
      `${variantCount} variant${variantCount !== 1 ? "s" : ""}`,
      `${sessionCount} session${sessionCount !== 1 ? "s" : ""}`,
      <ButtonGroup key={campaign.id}>
        <Button size="slim" onClick={() => navigate(`/campaigns/${campaign.id}`)}>
          Edit
        </Button>
        {campaign.status === "ACTIVE" && (
          <Button
            size="slim"
            onClick={() => handleStatusChange(campaign.id, "PAUSED")}
          >
            Pause
          </Button>
        )}
        {campaign.status === "PAUSED" && (
          <Button
            size="slim"
            onClick={() => handleStatusChange(campaign.id, "ACTIVE")}
          >
            Resume
          </Button>
        )}
        {campaign.status === "DRAFT" && (
          <Button
            size="slim"
            onClick={() => handleStatusChange(campaign.id, "ACTIVE")}
            variant="primary"
          >
            Activate
          </Button>
        )}
        <Button
          size="slim"
          tone="critical"
          onClick={() => handleDelete(campaign.id)}
        >
          Archive
        </Button>
      </ButtonGroup>,
    ];
  });

  if (loading) {
    return (
      <Page title="Campaigns">
        <Card>
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <Spinner size="large" />
          </div>
        </Card>
      </Page>
    );
  }

  if (error) {
    return (
      <Page
        title="Campaigns"
        primaryAction={{ content: "Create campaign", onAction: () => navigate("/campaigns/new") }}
      >
        <Banner tone="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      </Page>
    );
  }

  if (campaigns.length === 0) {
    return (
      <Page
        title="Campaigns"
        primaryAction={{ content: "Create campaign", onAction: () => navigate("/campaigns/new") }}
      >
        <Card>
          <EmptyState
            heading="No campaigns created yet"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            action={{ content: "Create campaign", onAction: () => navigate("/campaigns/new") }}
          >
            <p>Create your first campaign to start engaging customers with games.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title="Campaigns"
      subtitle={`${campaigns.length} campaign${campaigns.length !== 1 ? "s" : ""}`}
      primaryAction={{ content: "Create campaign", onAction: () => navigate("/campaigns/new") }}
    >
      {error && (
        <Banner tone="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      <Card>
        <DataTable
          columnContentTypes={["text", "text", "text", "text", "text", "text"]}
          headings={["Name", "Placement", "Status", "Variants", "Sessions", "Actions"]}
          rows={rows}
        />
      </Card>
    </Page>
  );
}

export default CampaignsListPage;
